// pages/AdminCustomWorkers/AdminCustomWorkers.jsx
//
// Super-admin queue for the custom-worker marketplace: read each brand's spec,
// quote a recurring price + start date (optionally binding it to a concrete
// workerKey), accept a brand's counter-offer (→ opens their Stripe Checkout),
// or decline. Super-admin only. Backend: /api/custom-worker-requests/admin/*.

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { get, post } from "../../api";

const PERIODS = ["monthly", "yearly"];
const STATUS_LABEL = {
  pending: "New", quoted: "Quoted", countered: "Counter from brand", accepted: "Agreed",
  awaiting_payment: "Awaiting payment", active: "Active", rejected: "Declined", cancelled: "Cancelled",
};
const STATUS_COLOR = {
  pending: "#2563eb", quoted: "#6b7280", countered: "#d97706", accepted: "#059669",
  awaiting_payment: "#7c3aed", active: "#059669", rejected: "#dc2626", cancelled: "#9ca3af",
};
const FILTERS = ["all", "pending", "quoted", "countered", "awaiting_payment", "active", "rejected"];

function money(p, period) { return `$${Number(p || 0).toLocaleString()}/${period || "monthly"}`; }
function fmtDate(d) { return d ? new Date(d).toDateString() : "—"; }
// Super-admin can (re)quote while still negotiating, accept only the brand's
// standing counter, and decline anything not yet terminal.
function canQuote(r) { return ["pending", "quoted", "countered"].includes(r.status); }
function canAcceptCounter(r) { return r.status === "countered" && r.lastOfferBy === "user"; }
function canDecline(r) { return !["active", "rejected", "cancelled"].includes(r.status); }

export default function AdminCustomWorkers() {
  const userInfo = useSelector((s) => s.user.userInfo);
  const isSuperAdmin = !!(userInfo && userInfo.superAdmin);

  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quoteFor, setQuoteFor] = useState(null);
  const [quote, setQuote] = useState({ price: "", period: "monthly", startDate: "", note: "", workerKey: "" });

  const fetchList = useCallback(async () => {
    try {
      const qs = filter === "all" ? "" : `?status=${filter}`;
      const r = await get(`/api/custom-worker-requests/admin/all${qs}`);
      setRows((r && r.requests) || []);
      setErr(null);
    } catch (e) { setErr((e && e.message) || "Failed to load"); }
  }, [filter]);

  useEffect(() => { if (isSuperAdmin) fetchList(); }, [fetchList, isSuperAdmin]);

  if (!isSuperAdmin) return <Redirect to="/dashboard" />;

  const act = async (id, path, body) => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await post(`/api/custom-worker-requests/${id}/${path}`, body || {}, false);
      if (path === "super-accept" && r && r.checkoutUrl) setMsg("Accepted — payment link sent to the brand.");
      setQuoteFor(null);
      await fetchList();
    } catch (e) { setErr((e && e.message) || "Action failed"); }
    finally { setBusy(false); }
  };

  const openQuote = (r) => {
    setQuoteFor(quoteFor === r._id ? null : r._id);
    setQuote({ price: r.price || "", period: r.period || "monthly", startDate: "", note: "", workerKey: r.workerKey || "" });
  };

  const submitQuote = (id) => {
    if (!(Number(quote.price) > 0)) return setErr("Enter a price greater than 0");
    if (!quote.startDate) return setErr("Pick a start date");
    return act(id, "quote", quote);
  };

  return (
    <div className="container py-4">
      <style>{`
        .acw-card { border:1px solid #e5e7eb; border-radius:10px; padding:16px; margin-bottom:14px; background:#fff; }
        .acw-badge { color:#fff; border-radius:999px; padding:2px 10px; font-size:12px; font-weight:600; }
        .acw-btn { border:none; border-radius:8px; padding:7px 14px; font-weight:600; cursor:pointer; margin-right:8px; }
        .acw-btn.primary { background:#2563eb; color:#fff; } .acw-btn.green { background:#059669; color:#fff; }
        .acw-btn.ghost { background:#f3f4f6; color:#374151; } .acw-btn.danger { background:#fee2e2; color:#b91c1c; }
        .acw-btn:disabled { opacity:.5; cursor:not-allowed; }
        .acw-field { display:block; width:100%; margin:6px 0 12px; padding:8px 10px; border:1px solid #d1d5db; border-radius:8px; }
        .acw-label { font-size:13px; font-weight:600; color:#374151; }
        .acw-spec { font-size:13px; color:#374151; margin:3px 0; }
        .acw-row { display:flex; gap:12px; flex-wrap:wrap; } .acw-row > div { flex:1; min-width:150px; }
        .acw-pill { background:#f3f4f6; border-radius:6px; padding:1px 8px; font-size:12px; margin-right:6px; cursor:pointer; }
        .acw-pill.on { background:#2563eb; color:#fff; }
      `}</style>

      <h3 style={{ marginBottom: 4 }}>Custom Workers — Admin</h3>
      <div style={{ marginBottom: 14 }}>
        {FILTERS.map((f) => <span key={f} className={`acw-pill ${filter === f ? "on" : ""}`} onClick={() => setFilter(f)}>{f}</span>)}
      </div>

      {err && <div className="alert alert-warning">{err}</div>}
      {msg && <div className="alert alert-success">{msg}</div>}
      {rows.length === 0 && <div style={{ color: "#6b7280" }}>No requests in this view.</div>}

      {rows.map((r) => {
        const brandCountered = canAcceptCounter(r);
        return (
          <div className="acw-card" key={r._id}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>{r.title}</strong>
              <span className="acw-badge" style={{ background: STATUS_COLOR[r.status] || "#6b7280" }}>{STATUS_LABEL[r.status] || r.status}</span>
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 6 }}>company {r.company}{r.brand_id ? ` · brand ${r.brand_id}` : ""}{r.workerKey ? ` · key ${r.workerKey}` : ""}</div>

            {r.jobDescription && <div className="acw-spec"><b>Job:</b> {r.jobDescription}</div>}
            {r.inputDescription && <div className="acw-spec"><b>Input:</b> {r.inputDescription}</div>}
            {r.outputDescription && <div className="acw-spec"><b>Output:</b> {r.outputDescription}</div>}
            {r.exampleInput && <div className="acw-spec"><b>Example in:</b> {r.exampleInput}</div>}
            {r.exampleOutput && <div className="acw-spec"><b>Example out:</b> {r.exampleOutput}</div>}
            {r.deliveryExplanation && <div className="acw-spec"><b>Delivery:</b> {r.deliveryExplanation}</div>}
            {Array.isArray(r.steps) && r.steps.length > 0 && <div className="acw-spec"><b>Steps:</b> {r.steps.join(" → ")}</div>}

            {(r.price > 0) && <div className="acw-spec" style={{ marginTop: 6 }}>Current terms: <b>{money(r.price, r.period)}</b> · start {fmtDate(r.startDate)} · last offer by {r.lastOfferBy === "super" ? "Prigmar" : "brand"}</div>}
            {r.status === "active" && <div className="acw-spec" style={{ color: "#059669" }}>Active · available {fmtDate(r.availabilityDate)}</div>}

            <div style={{ marginTop: 10 }}>
              {canQuote(r) && <button className="acw-btn primary" disabled={busy} onClick={() => openQuote(r)}>{r.status === "pending" ? "Quote" : "Re-quote"}</button>}
              {brandCountered && <button className="acw-btn green" disabled={busy} onClick={() => act(r._id, "super-accept")}>Accept counter</button>}
              {canDecline(r) &&
                <button className="acw-btn danger" disabled={busy} onClick={() => { const reason = window.prompt("Reason for declining (optional):", ""); if (reason !== null) act(r._id, "reject", { reason }); }}>Decline</button>}
            </div>

            {quoteFor === r._id && (
              <div className="acw-card" style={{ marginTop: 10, background: "#f9fafb" }}>
                <div className="acw-row">
                  <div><span className="acw-label">Price (USD)</span>
                    <input type="number" className="acw-field" value={quote.price} onChange={(e) => { const _v = e.target.value; setQuote((q) => ({ ...q, price: _v })); }} /></div>
                  <div><span className="acw-label">Period</span>
                    <select className="acw-field" value={quote.period} onChange={(e) => { const _v = e.target.value; setQuote((q) => ({ ...q, period: _v })); }}>
                      {PERIODS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select></div>
                  <div><span className="acw-label">Start date</span>
                    <input type="date" className="acw-field" value={quote.startDate} onChange={(e) => { const _v = e.target.value; setQuote((q) => ({ ...q, startDate: _v })); }} /></div>
                </div>
                <span className="acw-label">Bind to worker key (optional)</span>
                <input className="acw-field" value={quote.workerKey} onChange={(e) => { const _v = e.target.value; setQuote((q) => ({ ...q, workerKey: _v })); }} placeholder="e.g. bidlight-compare-bids" />
                <span className="acw-label">Note (optional)</span>
                <input className="acw-field" value={quote.note} onChange={(e) => { const _v = e.target.value; setQuote((q) => ({ ...q, note: _v })); }} />
                <button className="acw-btn primary" disabled={busy} onClick={() => submitQuote(r._id)}>Send quote</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

AdminCustomWorkers._test = { money, fmtDate, canQuote, canAcceptCounter, canDecline };
